import apiClient from './ApiClient';

/**
 * Service for daily reports API endpoints
 * Handles daily summaries, revenue reports, and operational metrics
 */
class DailyReportsService {
  constructor() {
    this.cache = {
      dailyReports: new Map(),
      lastFetch: new Map(),
      promise: new Map()
    };
    this.CACHE_DURATION = 10 * 60 * 1000; // 10 minutes cache for reports
    this.MAX_CACHE_SIZE = 50; // Max entries to prevent unbounded growth
  }

  /**
   * Get daily summary report for a specific date
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise} Promise resolving to daily report data
   */
  async getDailySummary(date, forceRefresh = false) {
    const now = Date.now();
    const cacheKey = `daily_${date}`;
    
    // Check if we have valid cached data
    const hasValidCache = this.cache.dailyReports.has(cacheKey) && 
                         this.cache.lastFetch.has(cacheKey) && 
                         (now - this.cache.lastFetch.get(cacheKey)) < this.CACHE_DURATION;
    
    if (!forceRefresh && hasValidCache) {
      return Promise.resolve(this.cache.dailyReports.get(cacheKey));
    }

    // If there's already a request in progress, return that promise
    if (this.cache.promise.has(cacheKey)) {
      return this.cache.promise.get(cacheKey);
    }

    // Make new request
    
    // Use real API endpoint
    const promise = apiClient.get('/v1/reports/daily-summary/', { date })
      .then(response => {
        const data = response;
        // Evict oldest entry if cache exceeds max size
        if (this.cache.dailyReports.size >= this.MAX_CACHE_SIZE) {
          const firstKey = this.cache.dailyReports.keys().next().value;
          this.cache.dailyReports.delete(firstKey);
          this.cache.lastFetch.delete(firstKey);
        }
        this.cache.dailyReports.set(cacheKey, data);
        this.cache.lastFetch.set(cacheKey, now);
        return data;
      })
      .catch(error => {
        console.error('DailyReportsService: Error fetching daily summary:', error);
        throw error;
      })
      .finally(() => {
        this.cache.promise.delete(cacheKey);
      });

    this.cache.promise.set(cacheKey, promise);
    return promise;
  }

  /**
   * Mock daily summary data - replace with real API call later
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise} Promise resolving to mock report data
   */
  async getMockDailySummary(date) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const selectedDate = new Date(date);
    const dayOfWeek = selectedDate.getDay();
    
    // Generate realistic mock data based on day of week
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const baseOccupancy = isWeekend ? 85 : 70;
    const baseRevenue = isWeekend ? 12000 : 8000;

    return {
      date: date,
      revenue: {
        room_revenue: baseRevenue + (Math.random() * 2000 - 1000),
        tax_amount: (baseRevenue * 0.12) + (Math.random() * 200 - 100),
        total_revenue: baseRevenue * 1.12 + (Math.random() * 2200 - 1100),
        adr: 120 + (Math.random() * 40 - 20),
        change_from_yesterday: Math.random() * 20 - 10 // -10% to +10%
      },
      occupancy: {
        occupied_rooms: Math.floor((baseOccupancy + (Math.random() * 10 - 5)) * 0.8),
        available_rooms: Math.floor(80 * ((100 - baseOccupancy) / 100)),
        occupancy_rate: Math.floor(baseOccupancy + (Math.random() * 10 - 5)),
        out_of_order_rooms: Math.floor(Math.random() * 3),
        rate_change: Math.random() * 10 - 5 // -5% to +5%
      },
      guest_activity: {
        check_ins: Math.floor(20 + (Math.random() * 15)),
        check_outs: Math.floor(18 + (Math.random() * 12)),
        no_shows: Math.floor(Math.random() * 3),
        walk_ins: Math.floor(Math.random() * 5),
        cancellations: Math.floor(Math.random() * 4)
      },
      outstanding: {
        unpaid_amount: Math.floor(Math.random() * 5000),
        pending_checkins: Math.floor(Math.random() * 8),
        overdue_checkouts: Math.floor(Math.random() * 3)
      },
      recent_transactions: this.generateMockTransactions(10)
    };
  }

  /**
   * Generate mock transaction data
   * @param {number} count - Number of transactions to generate
   * @returns {Array} Array of mock transactions
   */
  generateMockTransactions(count) {
    const transactions = [];
    const guestNames = [
      'John Smith', 'Sarah Johnson', 'Michael Brown', 'Emily Davis',
      'David Wilson', 'Lisa Anderson', 'Robert Taylor', 'Jennifer Martinez'
    ];
    
    const transactionTypes = ['payment', 'charge', 'refund', 'adjustment'];
    
    for (let i = 0; i < count; i++) {
      const now = new Date();
      const transactionTime = new Date(now.getTime() - (Math.random() * 24 * 60 * 60 * 1000));
      
      transactions.push({
        id: i + 1,
        guest_name: guestNames[Math.floor(Math.random() * guestNames.length)],
        type: transactionTypes[Math.floor(Math.random() * transactionTypes.length)],
        amount: Math.floor(Math.random() * 500 + 50),
        created_at: transactionTime.toISOString(),
        description: 'Room charge'
      });
    }
    
    return transactions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  /**
   * Get revenue summary for date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} Promise resolving to revenue summary
   */
  async getRevenueSummary(startDate, endDate) {
    try {
      // Use real API endpoint
      const response = await apiClient.get('/v1/reports/revenue-summary/', { startDate, endDate });
      return response;
    } catch (error) {
      console.error('DailyReportsService: Error fetching revenue summary:', error);
      throw error;
    }
  }

  /**
   * Mock revenue summary - replace with real API call later
   */
  async getMockRevenueSummary(startDate, endDate) {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    return {
      period: `${startDate} to ${endDate}`,
      total_revenue: Math.floor(Math.random() * 50000 + 20000),
      room_revenue: Math.floor(Math.random() * 40000 + 15000),
      tax_revenue: Math.floor(Math.random() * 8000 + 3000),
      average_daily_rate: Math.floor(Math.random() * 50 + 100),
      occupancy_rate: Math.floor(Math.random() * 30 + 60)
    };
  }

  /**
   * Get pending check-ins and overdue checkouts
   * @returns {Promise} Promise resolving to pending/overdue data
   */
  async getPendingAndOverdue() {
    try {
      const response = await apiClient.get('/v1/reports/pending-overdue/');
      return response;
    } catch (error) {
      console.error('DailyReportsService: Error fetching pending/overdue:', error);
      throw error;
    }
  }

  /**
   * Get payment report with filters
   * @param {string} startDate - Start date YYYY-MM-DD
   * @param {string} endDate - End date YYYY-MM-DD
   * @param {string} paymentMode - Filter: all, cash, card, upi, bank_transfer, other
   * @returns {Promise}
   */
  async getPaymentReport(startDate, endDate, paymentMode = 'all', search = '', extra = {}) {
    try {
      const params = { startDate, endDate, paymentMode };
      if (search) params.search = search;
      // Shift-close filters: staffId (one cashier's drawer), startTime/endTime (HH:MM)
      if (extra.staffId) params.staffId = extra.staffId;
      if (extra.startTime) params.startTime = extra.startTime;
      if (extra.endTime) params.endTime = extra.endTime;
      // One cash-counter shift: pins cashier + exact open→close window server-side
      if (extra.shiftId) params.shiftId = extra.shiftId;
      const response = await apiClient.get('/v1/reports/payment-report/', params);
      return response;
    } catch (error) {
      console.error('DailyReportsService: Error fetching payment report:', error);
      throw error;
    }
  }

  /**
   * Get housekeeping report with filters
   * @param {string} startDate - Start date YYYY-MM-DD
   * @param {string} endDate - End date YYYY-MM-DD
   * @param {string} status - Filter: all, not_cleaned, cleaned
   * @returns {Promise}
   */
  async getHousekeepingReport(startDate, endDate, status = 'all') {
    try {
      const response = await apiClient.get('/v1/reports/housekeeping-report/', {
        startDate, endDate, status
      });
      return response;
    } catch (error) {
      console.error('DailyReportsService: Error fetching housekeeping report:', error);
      throw error;
    }
  }

  /**
   * Clear cached data
   * @param {string} cacheKey - Specific cache key to clear, or null to clear all
   */
  clearCache(cacheKey = null) {
    if (cacheKey) {
      this.cache.dailyReports.delete(cacheKey);
      this.cache.lastFetch.delete(cacheKey);
      this.cache.promise.delete(cacheKey);
    } else {
      this.cache.dailyReports.clear();
      this.cache.lastFetch.clear();
      this.cache.promise.clear();
    }
  }
}

// Create and export a singleton instance
const dailyReportsService = new DailyReportsService();
export default dailyReportsService;